/**
 * #2091 — the chat UI preview flag, and the fake data behind it.
 *
 * ══ THE FLAG ══
 *
 * `CHAT_UI_PREVIEW`, a build-time value injected by webpack's DefinePlugin —
 * the same route `WIJMO_LICENSE_KEY` takes (#1458): a GitLab CI/CD variable ->
 * `config/prod.env.js` -> `process.env` in the bundle.
 *
 * ⚠ OFF UNLESS IT IS EXACTLY "1" OR "true". Not "truthy": an unset DefinePlugin
 * key, an empty string, "0" and "false" all read as off. This flag is the only
 * thing standing between production users and an unfinished chat UI, so the
 * failure mode when someone sets it to the wrong thing must be "stays off".
 *
 * ⚠ AND IT IS READ ONCE, AT MODULE LOAD. A build either has the preview or it
 * does not; nothing at runtime can turn it on, so there is no state to get out
 * of step and no way for a user to flip it from a console.
 *
 * ══ WHAT IS IN HERE ══
 *
 * Every byte of chat data in this preview is invented and lives in this file.
 * There is no chat back end yet (#6-#10, #18), so the UI must not call one: no
 * service import, no fetch, nothing persisted. Keeping the fakes in one module
 * rather than scattered through components means the day the real data arrives,
 * the thing to delete is obvious.
 *
 * No real person, local or job appears here.
 */

/** True only for a build that explicitly asked for the preview. */
export function chatUiEnabled() {
    const flag = String(process.env.CHAT_UI_PREVIEW || '').trim().toLowerCase();

    return flag === '1' || flag === 'true';
}

/**
 * #2096 — is this build wired to the REAL chat service?
 *
 * A SECOND flag, deliberately, rather than a new meaning for CHAT_UI_PREVIEW.
 * The two answer different questions and have different risks:
 *
 *   CHAT_UI_PREVIEW  may this user see the chat UI at all
 *   CHAT_LIVE        does that UI talk to cs3-chat, or to the fakes below
 *
 * So a build can show the preview with invented data (what Ed reviewed on
 * stage- for #2091) or show the same UI against the stage chat server, and
 * turning the second one on cannot happen by turning the first one on.
 *
 * ⚠ LIVE IMPLIES PREVIEW. `chatLiveEnabled()` is false unless CHAT_UI_PREVIEW
 * is also on, so there is no combination of variables that puts live chat in
 * front of a user who was never meant to see the panel. Read once at module
 * load, same as the flag above — nothing at runtime can flip it.
 *
 * Same "exactly 1 or true" rule, for the same reason: every other value,
 * including an unset DefinePlugin key, must read as off.
 */
export function chatLiveEnabled() {
    const flag = String(process.env.CHAT_LIVE || '').trim().toLowerCase();

    return chatUiEnabled() && (flag === '1' || flag === 'true');
}

/**
 * Who may see Configuration -> Chat: the Client Admin who owns the settings,
 * and CallSteward staff. Everyone else must not see the item at all — not a
 * disabled one, not an empty one.
 */
export function canAdminChat(roleSlug) {
    return roleSlug === 'client-admin' || roleSlug === 'super-admin';
}

/**
 * ONE shared settings object for the preview, so Configuration -> Chat and the
 * panel are the same screen's two halves rather than two unrelated demos.
 *
 * This is what makes every wireframe state reachable through the REAL UI
 * instead of needing mock-up controls bolted onto the app:
 *
 *   turn chat off in Configuration -> the panel disappears
 *   close today in the schedule     -> the panel's composer goes read-only
 *   switch a group off              -> its channels leave the channel list
 *
 * ⚠ NO `Vue.observable`, AND NO VUE IMPORT, DELIBERATELY. Vue 2 walks whatever a
 * component's `data()` returns and makes it reactive in place, so the first of
 * the two components to mount does that for both — they hold the same object.
 * Importing Vue here would tie this module (a flag reader and some invented
 * data) to the framework for no gain, and would stop it being loadable in a
 * plain node test.
 *
 * Keys that appear LATER still need `this.$set`, which is what both components
 * use for the group and role maps.
 *
 * In memory only: a reload resets everything, which the settings screen says.
 */
let _store = null;

export function previewStore() {
    if (!_store) {
        _store = fakeSettings();
    }

    return _store;
}

/** Test seam: forget the shared store so a case starts from the defaults. */
export function resetPreviewStore() {
    _store = null;
}

/**
 * Is the client closed right now, per its own schedule?
 *
 * Reads the real clock against the schedule the CA set, so the read-only state
 * is the genuine behaviour rather than a toggle — and editing Monday's hours in
 * Configuration changes what the panel does, which is the point.
 */
export function isOutsideHours(hours, now = new Date()) {
    if (!hours || !hours.on) return false;

    const day = hours.days[now.getDay()];          // getDay(): 0 = Sunday, and days[] starts at Sunday
    if (!day || !day.open) return true;

    const mins = now.getHours() * 60 + now.getMinutes();

    return mins < toMinutes(day.from) || mins >= toMinutes(day.to);
}

/** "Mon 08:00" — the next moment the client is open, walked forward from now. */
export function nextOpenLabel(hours, now = new Date()) {
    if (!hours || !hours.on) return null;

    const mins = now.getHours() * 60 + now.getMinutes();

    for (let i = 0; i <= 7; i++) {
        const day = hours.days[(now.getDay() + i) % 7];
        if (!day.open) continue;
        if (i === 0 && toMinutes(day.from) <= mins) continue;   // today, already past opening

        return day.short + ' ' + day.from;
    }

    return null;   // every day closed
}

function toMinutes(hhmm) {
    return parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(3, 5), 10);
}

/**
 * #2091 r3 — a job channel is chatable only from 24 h before the job starts to
 * 24 h after it ends (Ed, 12:38).
 *
 * Returns the window and which side of it we are on, so the panel can say
 * "Chat open Tue 08:00 → Thu 18:00" and refuse posting outside it.
 *
 * The hours either side are the point: crews talk about a call the night before
 * and settle up the morning after, and a channel that closed on the dot of the
 * last shift would cut both conversations off.
 */
export const JOB_CHAT_LEAD_MS = 24 * 60 * 60 * 1000;

export function jobChatWindow(job, now = new Date()) {
    if (!job || !job.start || !job.end) return null;

    const opensAt = new Date(new Date(job.start).getTime() - JOB_CHAT_LEAD_MS);
    const closesAt = new Date(new Date(job.end).getTime() + JOB_CHAT_LEAD_MS);
    const t = now.getTime();

    return {
        opensAt,
        closesAt,
        state: t < opensAt.getTime() ? 'before' : (t > closesAt.getTime() ? 'after' : 'open'),
    };
}

/** "Tue 08:00" — short enough for a channel header. */
export function shortWhen(date) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return days[date.getDay()] + ' ' + String(date.getHours()).padStart(2, '0')
        + ':' + String(date.getMinutes()).padStart(2, '0');
}

/** "Chat open Tue 08:00 → Thu 18:00", for the channel header. */
export function windowLabel(win) {
    return win ? 'Chat open ' + shortWhen(win.opensAt) + ' → ' + shortWhen(win.closesAt) : '';
}

// ─────────────────────────────────────────────────────────────────────────────
// Fake data. Invented names only.
// ─────────────────────────────────────────────────────────────────────────────

export const FAKE_CLIENT = 'Harbourview Stage Local';

export const ROLE_LABEL = {
    ca: 'CA', pu: 'PU', steward: 'Steward', member: 'Member',
    employer: 'Employer',
    cs_admin: 'CS Admin', cs_support: 'CS Support',
};

export const ROLE_PLURAL = {
    ca: 'Client Admins', pu: 'Power Users', steward: 'Stewards', member: 'Members',
    // #2091 r5 — Ed, 17:32: "please add a switch for employers".
    employer: 'Employers',
};

export const ROLE_COVERS = {
    ca: 'Everyone with the Client Admin role',
    pu: 'Everyone with the Power User role',
    steward: 'Everyone with the Steward role',
    member: 'Everyone else in this client',
    // An employer is an OUTSIDE party, not "everyone else in this client" — so
    // this one is worded by who they are rather than by what is left over.
    employer: 'Employer and payroll contacts, in their own employer channel',
};

/**
 * #2091 r5 — the identities the preview panel can be viewed as.
 *
 * ⚠ THIS IS A PREVIEW CONTROL AND IT HAS NO REAL-APP COUNTERPART. The panel
 * normally takes its chat role from the signed-in CS3 role, which means a
 * reviewer signed in as a Super Admin is `cs_admin` — and CS staff are never
 * restricted by a client's schedule, so they can never see the outside-hours
 * read-only state at all. Ed asked (17:32) to see the EMPLOYER read-only state
 * specifically, and there is no way to show it without being able to look
 * through another role's eyes. Behind CHAT_UI_PREVIEW like the rest of this.
 *
 * Named people are offered as well as roles because the precedence the screen
 * claims — person, then role, then schedule — is only demonstrable if you can
 * be a named person.
 */
export const PREVIEW_ROLES = ['ca', 'pu', 'steward', 'member', 'employer', 'cs_admin'];

export const GROUP_SOURCE = {
    // #2091 r5 — Ed, 17:36: "Channel groups remove Client Admins". The CA
    // outside-hours ROLE switch is a different thing and stays; this is only the
    // channel group, so CAs no longer get a channel of their own.
    'Power Users': 'Everyone with the Power User role in CS3',
    'Stewards': 'Everyone with the Steward role in CS3',
    'All Members': 'Every member of this client',
    'Job': 'One per job call, from the job’s crew',
    'Employers / Payroll': 'Employer and payroll contacts',
};

/**
 * #2091 r3 — the two groups that are MASTER switches rather than simple on/off.
 *
 * Ed, 12:38: "Job and Employer/payroll swithces are clinet master swwitches to
 * job details chat option and an employer chat option. ie you need both to have
 * a channel."
 *
 * So the client switch does not create channels on its own — it only permits
 * them. The per-job / per-employer option is the second half, and a channel
 * exists only where both are on.
 */
export const MASTER_SWITCHES = {
    'Job': 'Permits job channels. Each job also needs its own Chat option on the job — both, or no channel.',
    'Employers / Payroll': 'Permits employer channels. Each employer also needs its own Chat option — both, or no channel.',
};

/** The client's employees, for the CA's search. Invented, contact details included. */
export const DIRECTORY = [
    { eid: 40118, name: 'Dana Whitfield', role: 'steward', phone: '(555) 0147-2210', email: 'd.whitfield@example.net' },
    { eid: 40204, name: 'Marcus Ellery', role: 'pu', phone: '(555) 0147-8841', email: 'm.ellery@example.net' },
    { eid: 40337, name: 'Priya Raghunath', role: 'member', phone: '(555) 0162-3390', email: 'p.raghunath@example.net' },
    { eid: 40412, name: 'Tom Okonkwo', role: 'member', phone: '(555) 0162-7755', email: 't.okonkwo@example.net' },
    { eid: 40455, name: 'Lena Vasquez', role: 'member', phone: '(555) 0188-2043', email: 'l.vasquez@example.net' },
    { eid: 40519, name: 'Cal Brennan', role: 'member', phone: '(555) 0188-6612', email: 'c.brennan@example.net' },
    { eid: 40588, name: 'Ruth Ngata', role: 'steward', phone: '(555) 0199-4417', email: 'r.ngata@example.net' },
    { eid: 40640, name: 'Ade Oyelaran', role: 'member', phone: '(555) 0199-9028', email: 'a.oyelaran@example.net' },
    { eid: 40702, name: 'Nina Kowalczyk', role: 'member', phone: '(555) 0203-1164', email: 'n.kowalczyk@example.net' },
    { eid: 40771, name: 'Owen Reyes', role: 'member', phone: '(555) 0203-5590', email: 'o.reyes@example.net' },
    { eid: 40814, name: 'Jo Marchetti', role: 'member', phone: '(555) 0217-3321', email: 'j.marchetti@example.net' },
    { eid: 40889, name: 'Hal Brandt', role: 'member', phone: '(555) 0217-8806', email: 'h.brandt@example.net' },
    { eid: 40923, name: 'Sofia Delgado', role: 'pu', phone: '(555) 0228-1172', email: 's.delgado@example.net' },
    { eid: 40977, name: 'Casey Lindgren', role: 'ca', phone: '(555) 0228-6640', email: 'c.lindgren@example.net' },
    // #2091 r5 — an employer contact, so the Employers switch can be overridden
    // for one person the same way any other role's can.
    { eid: 41032, name: 'Greta Lindqvist', role: 'employer', phone: '(555) 0241-7708', email: 'g.lindqvist@example.net' },
];

/** A fresh copy each time, so one screen's edits never leak into another's. */
export function fakeSettings() {
    return {
        chatOn: true,
        groups: {
            // #2091 r3 — client scope only. Skills, Status and Skills by Status
            // were removed on Ed's instruction (12:38): "channele groups are
            // limited to the client scope".
            // #2091 r5 — no 'Client Admins' group (Ed, 17:36).
            'Power Users': true, 'Stewards': true, 'All Members': true,
            'Job': true, 'Employers / Payroll': true,
        },
        grants: [
            { name: 'Marcus Ellery', role: 'pu', granted: true },
            { name: 'Dana Whitfield', role: 'steward', granted: true },
            { name: 'Ruth Ngata', role: 'steward', granted: false },
            { name: 'Priya Raghunath', role: 'member', granted: false },
        ],
        hours: {
            on: true,
            tz: 'America/New_York',
            tzFromClient: true,
            days: [
                { key: 'sun', label: 'Sunday', short: 'Sun', open: false, from: '09:00', to: '13:00' },
                { key: 'mon', label: 'Monday', short: 'Mon', open: true, from: '08:00', to: '18:00' },
                { key: 'tue', label: 'Tuesday', short: 'Tue', open: true, from: '08:00', to: '18:00' },
                { key: 'wed', label: 'Wednesday', short: 'Wed', open: true, from: '08:00', to: '18:00' },
                { key: 'thu', label: 'Thursday', short: 'Thu', open: true, from: '08:00', to: '18:00' },
                { key: 'fri', label: 'Friday', short: 'Fri', open: true, from: '08:00', to: '20:00' },
                { key: 'sat', label: 'Saturday', short: 'Sat', open: true, from: '09:00', to: '14:00' },
            ],
            // Allowed to post OUTSIDE office hours. CS staff are never restricted
            // and are deliberately not in this list.
            // #2091 r5 — `employer` starts OFF so the employer's read-only state
            // is what Ed sees first; turning it on is the other half of the demo.
            roles: { ca: true, pu: true, steward: false, member: false, employer: false },
            // Named exceptions beat the role switch, either way.
            people: [
                { eid: 40118, name: 'Dana Whitfield', role: 'steward', allowed: true },
                { eid: 40337, name: 'Priya Raghunath', role: 'member', allowed: true },
                { eid: 40204, name: 'Marcus Ellery', role: 'pu', allowed: false },
            ],
        },
    };
}

// ── the panel's fake conversation ────────────────────────────────────────────

export const PEOPLE = [
    { n: 'Dana Whitfield', i: 'DW', r: 'steward', c: '#7a5bb5' },
    { n: 'Marcus Ellery', i: 'ME', r: 'pu', c: '#b5553f' },
    { n: 'Priya Raghunath', i: 'PR', r: 'member', c: '#2f7d64' },
    { n: 'Tom Okonkwo', i: 'TO', r: 'member', c: '#3f6bbf' },
    { n: 'Lena Vasquez', i: 'LV', r: 'member', c: '#9a5b8c' },
    { n: 'Cal Brennan', i: 'CB', r: 'member', c: '#5b6b8c' },
    { n: 'Ruth Ngata', i: 'RN', r: 'steward', c: '#7a6b2f' },
    { n: 'Ade Oyelaran', i: 'AO', r: 'member', c: '#3f8c8c' },
    { n: 'Nina Kowalczyk', i: 'NK', r: 'member', c: '#8c5b3f' },
    { n: 'Owen Reyes', i: 'OR', r: 'member', c: '#5b8c3f' },
];

export const CS_STAFF = { n: 'Avery Lund', i: 'AL', r: 'cs_admin', c: '#2f7d64' };

/** An ISO timestamp N hours from now, so the fake windows track the real clock. */
function hoursFromNow(h) {
    return new Date(Date.now() + h * 60 * 60 * 1000).toISOString();
}

export const CHANNELS = [
    { id: 'cs_admin', grp: 'cs', name: 'cs-admin', label: 'CS Admin', unread: 2, see: ['cs_admin', 'cs_support'], last: 'Avery Lund: ticket 4192 is with the dev team', at: '09:12' },
    { id: 'cs_support', grp: 'cs', name: 'cs-support', label: 'CS Support', unread: 0, see: ['cs_admin', 'cs_support'], last: 'Sam Dorsett: closed the Riverside AV ticket', at: 'Fri' },

    { id: 'c7_role_pu', grp: 'client', name: 'power-users', label: 'Power Users', unread: 1, see: ['ca', 'pu', 'cs_admin', 'cs_support'], last: 'Marcus Ellery: who’s covering dispatch Sunday?', at: '08:58' },
    { id: 'c7_role_steward', grp: 'client', name: 'stewards', label: 'Stewards', unread: 0, see: ['ca', 'steward', 'cs_admin', 'cs_support'], last: 'Ruth Ngata: floor walk done, all clear', at: 'Sun' },
    { id: 'c7_all', grp: 'client', name: 'all-members', label: 'All Members', unread: 3, see: ['ca', 'pu', 'steward', 'member', 'cs_admin', 'cs_support'], last: 'Dana Whitfield: call sheet for Thursday is posted', at: '08:31' },



    /* #2091 r3 — three jobs, one on each side of the 24h-either-side window, and
       anchored to the REAL clock rather than fixed dates so the three states
       still hold whenever this is opened. `chatOn` is the job's OWN Chat option:
       the client master switch permits job channels, this one creates them, and
       a channel needs both. */
    {
        id: 'c7_job_18420', grp: 'client', name: 'job-18420',
        label: 'Job · 18420 Harbour Arena Load-in', unread: 7, demo: true,
        job: { chatOn: true, start: hoursFromNow(-18), end: hoursFromNow(10) },   // inside the window
        see: ['ca', 'pu', 'steward', 'member', 'cs_admin', 'cs_support'],
        last: 'Tom Okonkwo: dock is single-lane today', at: '09:01',
    },
    {
        id: 'c7_job_18444', grp: 'client', name: 'job-18444',
        label: 'Job · 18444 Riverside Hall Corporate AV', unread: 0,
        job: { chatOn: true, start: hoursFromNow(72), end: hoursFromNow(84) },    // opens in 2 days
        see: ['ca', 'pu', 'steward', 'member', 'cs_admin', 'cs_support'],
        last: 'CallSteward: chat opens 24 hours before the call', at: '—',
    },
    {
        id: 'c7_job_18377', grp: 'client', name: 'job-18377',
        label: 'Job · 18377 Civic Theatre Strike', unread: 0, archived: true,
        job: { chatOn: true, start: hoursFromNow(-120), end: hoursFromNow(-96) }, // closed 3 days ago
        see: ['ca', 'pu', 'steward', 'member', 'cs_admin', 'cs_support'],
        last: 'Owen Reyes: all clear, keys back to house', at: '11 Oct',
    },
    {
        id: 'c7_job_18431', grp: 'client', name: 'job-18431',
        label: 'Job · 18431 Civic Theatre Focus call', unread: 0,
        // The job's own Chat option is OFF, so no channel exists however the
        // client master switch is set. Listed here only so the rule is visible;
        // visibleChannels() drops it.
        job: { chatOn: false, start: hoursFromNow(-6), end: hoursFromNow(6) },
        see: ['ca', 'pu', 'steward', 'member', 'cs_admin', 'cs_support'],
        last: 'CallSteward: this job’s own Chat option is off, so there is no channel', at: '—',
    },

    {
        id: 'c7_employer_payroll', grp: 'client', name: 'employers-payroll',
        label: 'Employers / Payroll · Harbourview Events Co', unread: 0,
        employer: { chatOn: true },      // the employer's own Chat option
        // #2091 r5 — the employer is IN their own channel and nowhere else. They
        // are an outside party, so they get no member, job or global channel.
        see: ['ca', 'employer', 'cs_admin', 'cs_support'],
        last: 'You: September run sent Monday', at: 'Thu',
    },
    {
        id: 'c7_employer_riverside', grp: 'client', name: 'employers-riverside',
        label: 'Employers / Payroll · Riverside Hall AV', unread: 0,
        employer: { chatOn: false },     // off, so no channel even with the master on
        see: ['ca', 'employer', 'cs_admin', 'cs_support'],
        last: 'CallSteward: this employer’s own Chat option is off, so there is no channel', at: '—',
    },

    { id: 'g_riggers', grp: 'global', name: 'riggers', label: 'Riggers (CS world)', unread: 1, needsGrant: true, see: ['ca', 'pu', 'steward', 'cs_admin', 'cs_support'], last: 'Jo Marchetti (Eastport): anyone run this motor brand?', at: '08:44' },
    { id: 'g_dispatch', grp: 'global', name: 'dispatch-notices', label: 'Dispatch Notices', unread: 0, needsGrant: true, see: ['ca', 'pu', 'steward', 'cs_admin', 'cs_support'], last: 'CallSteward: holiday dispatch guidance posted', at: 'Fri' },
];

export const GROUPS = [
    { key: 'cs', title: 'CS Internal', staffOnly: true },
    { key: 'client', title: FAKE_CLIENT },
    { key: 'global', title: 'CS World · global' },
];

const CHATTER = [
    'Load-in call moved to 06:30 — truck’s in early.',
    'Need two more on the rail for the truss lift.',
    'FOH position is set and the snake run is clear.',
    'Who’s got the spot call for the matinee?',
    'Wardrobe is one dresser short for the quick-change.',
    'Deck crew: strike starts after the encore, not at curtain.',
    'Hard hats on for the chain-motor check, no exceptions.',
    'A-List riggers only on the high steel for this one.',
    'Shop pickup 05:45. Don’t be late, the gate closes.',
    'Second truck pushed to 14:00 — dock is single-lane today.',
    'Genie is booked out until 11:00, plan the focus around it.',
    'Audio world: we’re on the house console, not the tour desk.',
    'Dimmer rack 3 is throwing a fault, electrics is on it.',
    'Lunch is called at 12:00, back on the clock at 12:30.',
    'Keep the loading dock clear — fire lane inspection this morning.',
    'Two spots on the fly rail still open for Tuesday strike.',
    'Mark the cable runs before the house opens, please.',
    'Push the followspot call thirty minutes, band is late to soundcheck.',
    'Anyone got a spare 50-foot XLR in their kit?',
    'Steward walking the floor at 09:00 for the safety check.',
    'The truss is flown and dead-hung, you’re clear underneath.',
    'Catwalk access is through the SR stairwell, not the lift.',
    'Headsets are charged and in the case at the prompt desk.',
    'Do not strike the ground-support until the riggers sign off.',
    'Call sheet for Thursday is posted, check your own times.',
    'Wardrobe truck unloads at door 4, not the main dock.',
    'We’re one short on the morning deck call if anyone wants it.',
    'Stage left wing is blocked by road cases — clearing it now.',
    'Soundcheck ran long, house opens fifteen minutes late.',
    'Bring your own harness, the house ones are out for inspection.',
];

export const DAYS = [
    'Monday 13 October', 'Sunday 12 October', 'Saturday 11 October',
    'Friday 10 October', 'Thursday 9 October', 'Wednesday 8 October',
];

export const TODAY = DAYS[0];

/** The ten newest messages, each carrying a rule from spec §5. */
export const DEMO = [
    { who: PEOPLE[1], day: TODAY, at: '08:02', body: 'Morning. Harbour Arena load-in is 06:30 tomorrow, 24 called.' },
    {
        who: PEOPLE[0], day: TODAY, at: '08:04',
        body: 'Rigging plot is up on the venue page if anyone wants a look before the call.',
        link: { title: 'Harbour Arena — Technical Specifications & Rigging Plot', host: 'harbourarena.example.org', letter: 'H' },
    },
    { who: PEOPLE[6], day: TODAY, at: '08:11', body: 'Reminder from the safety meeting: hard hats for the whole chain-motor check, not just the people on the motors.' },
    { who: CS_STAFF, day: TODAY, at: '08:19', staff: true, body: 'Heads up from CallSteward — the Tuesday maintenance window moved to 02:00 ET. No action needed.' },
    {
        who: PEOPLE[3], day: TODAY, at: '08:24',
        body: 'Someone sent me this "schedule" link and it looks off:',
        link: { title: 'callsteward.com — Sign in', host: 'xn--callstward-9db.com', letter: '?', punycode: 'the name uses look-alike characters; shown in punycode' },
    },
    { who: PEOPLE[0], day: TODAY, at: '08:26', body: 'That’s not us. Don’t sign in to it — I’ll tell the office.' },
    {
        who: PEOPLE[4], day: TODAY, at: '08:40',
        body: 'Pay period cut-off is Sunday; the form’s on the union site.',
        link: { title: 'Member Resources — Forms and Deadlines', host: 'harbourviewstage.example.net', letter: 'H', svgRefused: true },
    },
    { who: PEOPLE[8], day: TODAY, at: '08:47', body: 'Pasted from the old call sheet, ignore the formatting:\n<b>LOAD-IN 06:30</b>  <i>deck crew report to SR</i>' },
    { who: PEOPLE[2], day: TODAY, at: '08:52', body: 'Mail me at priya.raghunath@example.net if the gate code doesn’t work — it’s in the job notes too.', mailtoNote: true },
    { who: PEOPLE[1], day: TODAY, at: '09:03', body: 'Two spots still open on the Tuesday strike call if anyone’s free.' },
];

/**
 * 119 generated messages, so the demo channel reaches exactly 120 with DEMO and
 * every other channel reaches 120 with its own closing line. The clock only
 * moves forward and a line never repeats inside a screenful: random-looking is
 * not the same as plausible, and timestamps that jump backwards read as broken.
 */
export function buildHistory() {
    let seed = 20261013;
    const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
    const pick = (a) => a[Math.floor(rnd() * a.length)];
    const hhmm = (mins) => String(Math.floor(mins / 60)).padStart(2, '0') + ':' + String(mins % 60).padStart(2, '0');

    const out = [];
    const recent = [];
    const perDay = Math.ceil(119 / (DAYS.length - 1));
    let dayIdx = DAYS.length - 1;
    let mins = 7 * 60 + Math.floor(rnd() * 25);
    let inDay = 0;

    for (let i = 0; i < 119; i++) {
        if (inDay >= perDay && dayIdx > 1) {
            dayIdx--; inDay = 0;
            mins = 7 * 60 + Math.floor(rnd() * 25);
        }
        let body;
        do { body = pick(CHATTER); } while (recent.indexOf(body) >= 0);
        recent.push(body);
        if (recent.length > 14) recent.shift();

        out.push({ who: pick(PEOPLE), day: DAYS[dayIdx], at: hhmm(mins), body });

        mins += 3 + Math.floor(rnd() * 26);
        if (mins > 20 * 60) mins = 20 * 60;
        inDay++;
    }

    return out;
}

const SHORT_DAY = { Sun: DAYS[1], Sat: DAYS[2], '11 Oct': DAYS[2], Fri: DAYS[3], Thu: DAYS[4] };

/**
 * Each channel ends on the message its list preview promised. Without this every
 * channel shows one shared conversation, and an archived strike channel appears
 * to be discussing someone else's pay period.
 */
export function historyFor(channel, generated, me) {
    if (channel.demo) {
        return generated.slice(9).concat(DEMO);
    }

    // A preview line is "Name: text". Fall back rather than slice at -1 if one
    // is ever written without the prefix — a mangled name is harder to spot
    // than a missing one.
    const cut = channel.last.indexOf(': ');
    const who = cut > 0 ? channel.last.slice(0, cut) : 'CallSteward';
    let body = cut > 0 ? channel.last.slice(cut + 2) : channel.last;
    const isTime = /^\d\d:\d\d$/.test(channel.at);

    let person = null;
    if (who === 'You') person = { n: me.name, i: me.initials, r: me.role, c: me.colour };
    else if (who.indexOf('CallSteward') === 0) person = { n: CS_STAFF.n, i: CS_STAFF.i, r: 'cs_admin', c: CS_STAFF.c };
    else person = PEOPLE.find((p) => p.n === who) || null;

    if (!person) {
        person = { n: who, i: who.slice(0, 1) + (who.split(' ')[1] || ' ').slice(0, 1), r: 'member', c: '#5b6b8c' };
    }

    body = body.charAt(0).toUpperCase() + body.slice(1);
    if (!/[.?!]$/.test(body)) body += '.';

    return generated.concat([{
        who: person,
        day: isTime ? TODAY : (SHORT_DAY[channel.at] || DAYS[2]),
        at: isTime ? channel.at : '17:40',
        body,
        staff: who.indexOf('CallSteward') === 0,
    }]);
}
